import React from 'react';
import { Section } from '../ui';
import { ChevronLeftIcon, ChevronRightIcon } from '../ui/icons';

const testimonials = [
  {
    quote:
      'BoostHiring allowed us to compare agency submissions clearly and hire faster without exceeding budget.',
    author: 'Sarah Jenkins',
    role: 'CEO, TechFlow Solutions',
    image:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=150&h=150',
    color: 'bg-primary text-white',
  },
  {
    quote:
      'Finally a platform that aligns incentives. The success-based model transformed how we work with agencies.',
    author: 'Michael Chen',
    role: 'Director of Talent, Logisticore',
    image:
      'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=150&h=150',
    color: 'bg-secondary text-white',
  },
  {
    quote:
      "The transparency is unmatched. We know exactly what we're paying for and getting high-quality candidates.",
    author: 'Emily Rodriguez',
    role: 'VP HR, GrowthBox',
    image:
      'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=150&h=150',
    color: 'bg-tertiary text-white',
  },
  {
    quote:
      'A game-changer for our recruitment process. We reduced our time-to-hire by 40% in just two months.',
    author: 'David Park',
    role: 'CTO, InnovateX',
    image:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=150&h=150',
    color: 'bg-coral text-white',
  },
  {
    quote:
      'The quality of candidates from the agency network has been consistently impressive. Highly recommended.',
    author: 'Lisa Thompson',
    role: 'Founder, Creative Pulse',
    image:
      'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=150&h=150',
    color: 'bg-secondary text-white',
  },
  {
    quote:
      'As an agency, BoostHiring gives us access to real opportunities without the usual business development headache.',
    author: 'James Wilson',
    role: 'Head of Recruitment, Skyline Partners',
    image:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=150&h=150',
    color: 'bg-tertiary text-white',
  },
  {
    quote:
      'Efficiency at its best. The unified dashboard makes managing multiple agency submissions effortless.',
    author: 'Anna Garcia',
    role: 'Operations Manager, Nexus Corp',
    image:
      'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=150&h=150',
    color: 'bg-primary text-white',
  },
  {
    quote:
      "We've scaled our agency significantly by leveraging the steady stream of verified job orders on BoostHiring.",
    author: 'Robert Martinez',
    role: 'Director, Zenith Agency',
    image:
      'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80&w=150&h=150',
    color: 'bg-coral text-white',
  },
];

// eslint-disable-next-line import/prefer-default-export
export function Testimonials() {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [resetKey, setResetKey] = React.useState(0);
  const goTo = (idx: number) => {
    setCurrentIndex(idx);
    setResetKey(k => k + 1);
  };
  const next = () => {
    goTo((currentIndex + 1) % testimonials.length);
  };
  const prev = () => {
    goTo((currentIndex - 1 + testimonials.length) % testimonials.length);
  };

  React.useEffect(() => {
    const interval = setInterval(next, 15000);
    return () => clearInterval(interval);
  }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = testimonials[currentIndex];

  return (
    <Section
      variant="transparent"
      className={`transition-colors duration-500 ${active.color} relative overflow-hidden`}
    >
      <div className="absolute top-10 left-10 md:left-20 text-9xl font-serif opacity-10 pointer-events-none leading-none">
        &ldquo;
      </div>
      <div className="max-w-4xl mx-auto px-4">
        <div
          key={currentIndex}
          className="relative z-10 flex flex-col items-center text-center animate-fade-in"
        >
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-white/20 shadow-lg mb-8 overflow-hidden">
            <img
              src={active.image}
              alt={active.author}
              width={150}
              height={150}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
          <blockquote className="text-2xl md:text-3xl font-medium leading-relaxed mb-8">
            &ldquo;{active.quote}&rdquo;
          </blockquote>
          <cite className="not-italic">
            <div className="font-bold text-lg">{active.author}</div>
            <div className="opacity-80 font-medium">{active.role}</div>
          </cite>
        </div>
        <div className="flex justify-center gap-3 mt-10">
          {testimonials.map((t, idx) => (
            <button
              type="button"
              key={t.author}
              onClick={() => goTo(idx)}
              className="p-2 transition-transform hover:scale-110 focus:outline-none"
              aria-label={`Go to testimonial ${idx + 1}`}
            >
              <div
                className={`w-3 h-3 rounded-full transition duration-500 ${
                  idx === currentIndex ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/60'
                }`}
              />
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={prev}
        className="absolute left-4 md:left-12 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-500 text-white hidden md:block"
        aria-label="Previous testimonial"
      >
        <ChevronLeftIcon className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={next}
        className="absolute right-4 md:right-12 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-500 text-white hidden md:block"
        aria-label="Next testimonial"
      >
        <ChevronRightIcon className="w-5 h-5" />
      </button>
    </Section>
  );
}
